// Core scan normalizers.

import { SCAN_STATUS, SCAN_TYPE, SITE_SCAN_PHASE } from 'src/services/consts/shared.const'
import {
  TRACKER_CATEGORY_UNCLASSIFIED_KEY,
  TRACKER_TYPES,
} from 'src/services/consts/scan/scan.const'

/**
 * @typedef {Object} ScanTracker
 * @property {string} name
 * @property {string} category - TRACKER_CATEGORY_UNCLASSIFIED_KEY when classified === false
 * @property {string|null} vendor - null when classified === false
 * @property {boolean} classified
 * @property {string[]} foundOnPages
 * @property {string|null} domain
 * @property {string} type - TRACKER_TYPES value
 */

/** @returns {ScanTracker|null} */
function normalizeScanTracker(raw, type) {
  if (!raw || typeof raw !== 'object' || !raw.name) return null
  return {
    name: raw.name,
    category: raw.category || TRACKER_CATEGORY_UNCLASSIFIED_KEY,
    vendor: raw.vendor ?? null,
    classified: raw.classified ?? false,
    foundOnPages: Array.isArray(raw.foundOnPages) ? raw.foundOnPages : [],
    domain: raw.domain || raw.origin || null,
    type,
  }
}

/** @returns {ScanTracker[]} */
function normalizeScanTrackerList(items, type) {
  return Array.isArray(items)
    ? items.map((item) => normalizeScanTracker(item, type)).filter(Boolean)
    : []
}

/**
 * @typedef {Object} ScanResource - shared shape for scripts + iframes
 * @property {string|null} src
 * @property {string|null} category
 * @property {string|null} vendor
 * @property {boolean} classified
 * @property {string[]} foundOnPages
 */

/**
 * @typedef {Object} ScanSummary
 * @property {number} totalItems
 * @property {number} classifiedItems
 * @property {number} unclassifiedItems
 * @property {number} classifiedCookies
 * @property {number} totalScripts
 * @property {number} classifiedScripts
 * @property {number} totalInlineScripts
 * @property {number} classifiedInlineScripts
 * @property {number} totalIframes
 * @property {number} classifiedIframes
 * @property {number} totalTrackingPixels
 * @property {number} classifiedTrackingPixels
 */

/**
 * @typedef {Object} ScanResults
 * @property {ScanTracker[]} trackers
 * @property {Object[]} inlineScripts
 * @property {ScanResource[]} scripts
 * @property {ScanResource[]} iframes
 * @property {Object[]} trackingPixels
 * @property {ScanSummary|null} summary
 */

/**
 * @typedef {Object} OverrideScanCategoryPayload
 * @property {Array<{ name: string, domain?: string|null, category: string }>} [cookies]
 * @property {Array<{ name: string, kind: string, origin?: string|null, category: string }>} [webStorage] - `kind` is a TRACKER_TYPES storage value
 * @property {Array<{ src: string, category: string }>} [scripts]
 */

/** @param {string|null|undefined} status */
export function isActiveScanStatus(status) {
  return status === SCAN_STATUS.PENDING || status === SCAN_STATUS.SCANNING
}

/** Unknown/missing values default to FULL — matches backend contract. */
export function normalizeScanType(scanType) {
  return scanType === SCAN_TYPE.PREVIEW ? SCAN_TYPE.PREVIEW : SCAN_TYPE.FULL
}

/**
 * @typedef {Object} ScanProgressCounts - live mid-scan item counts (pre-classification); null once classified results land
 * @property {number} cookies
 * @property {number} indexedDB
 * @property {number} localStorage
 * @property {number} sessionStorage
 * @property {number} totalItems
 */

/** @returns {ScanProgressCounts|null} */
export function normalizeScanProgress(raw) {
  if (!raw || typeof raw !== 'object') return null
  return {
    cookies: raw.cookies ?? 0,
    indexedDB: raw.indexedDB ?? 0,
    localStorage: raw.localStorage ?? 0,
    sessionStorage: raw.sessionStorage ?? 0,
    totalItems: raw.totalItems ?? 0,
  }
}

/** @returns {ScanResults|null} */
export function normalizeScanResults(raw) {
  if (!raw || typeof raw !== 'object') return null

  const cookieTrackers = normalizeScanTrackerList(raw.cookies, TRACKER_TYPES.COOKIE)
  const indexedDbTrackers = normalizeScanTrackerList(
    raw.webStorage?.indexedDB,
    TRACKER_TYPES.INDEXED_DB
  )
  const localStorageTrackers = normalizeScanTrackerList(
    raw.webStorage?.localStorage,
    TRACKER_TYPES.LOCAL_STORAGE
  )
  const sessionStorageTrackers = normalizeScanTrackerList(
    raw.webStorage?.sessionStorage,
    TRACKER_TYPES.SESSION_STORAGE
  )

  return {
    trackers: [
      ...cookieTrackers,
      ...indexedDbTrackers,
      ...localStorageTrackers,
      ...sessionStorageTrackers,
    ],
    inlineScripts: Array.isArray(raw.inlineScripts) ? raw.inlineScripts : [],
    scripts: Array.isArray(raw.scripts) ? raw.scripts : [],
    iframes: Array.isArray(raw.iframes) ? raw.iframes : [],
    trackingPixels: Array.isArray(raw.trackingPixels) ? raw.trackingPixels : [],
    summary: raw.summary ?? null,
  }
}

/**
 * @typedef {Object} ScanStatus
 * @property {string} status - SCAN_STATUS value ('scanning' | 'completed' | ...)
 * @property {string|null} scanId
 * @property {string|null} siteId
 * @property {string|null} url
 * @property {string} scanType - SCAN_TYPE value ('preview' | 'full')
 * @property {number|null} pagesScanned
 * @property {number|null} totalPagesFound
 * @property {string|null} startedAt
 * @property {string|null} completedAt
 * @property {string|null} error
 * @property {ScanResults|null} results
 * @property {ScanResults|null} previewScan
 * @property {ScanProgressCounts|null} progress
 */

/** @returns {ScanStatus|null} */
export function normalizeScanStatus(raw) {
  if (!raw || typeof raw !== 'object') return null

  const status = raw.status ?? (raw.error ? SCAN_STATUS.FAILED : null)
  if (!status) return null

  return {
    status,
    scanId: raw.scanId ?? null,
    siteId: raw.siteId ?? null,
    url: raw.url ?? null,
    scanType: normalizeScanType(raw.scanType),
    pagesScanned: raw.pagesScanned ?? null,
    totalPagesFound: raw.totalPagesFound ?? null,
    startedAt: raw.startedAt ?? null,
    completedAt: raw.completedAt ?? null,
    error: raw.error ?? null,
    results: normalizeScanResults(raw.results),
    // `previewScan` on the wire is a full scan document (its own status/scanId/progress) —
    // only its classified `results` are relevant here, so unwrap before normalizing.
    previewScan: normalizeScanResults(raw.previewScan?.results),
    progress: normalizeScanProgress(raw.progress),
  }
}

/**
 * Optimistic scan status for trigger-scan cache seeding.
 *
 * @param {{ siteId: string, url?: string|null, scanType?: string }} input
 * @returns {ScanStatus}
 */
export function createPendingScanStatus({ siteId, url = null, scanType = SCAN_TYPE.FULL }) {
  return {
    status: SCAN_STATUS.PENDING,
    scanId: null,
    siteId,
    url,
    scanType,
    pagesScanned: null,
    totalPagesFound: null,
    startedAt: new Date().toISOString(),
    completedAt: null,
    error: null,
    results: null,
    previewScan: null,
    progress: null,
  }
}

/**
 * Backend semantics: `lastScanned` means "last FULL scan completed." A preview can
 * populate tracker counts before any full scan exists, so `lastScanned == null` with
 * real counts means "preview done, full scan running" — not "never scanned."
 *
 * | lastScanned | counts  | phase                        |
 * |-------------|---------|------------------------------|
 * | null        | null    | NEVER_SCANNED                |
 * | null        | numbers | PREVIEW_DONE_FULL_RUNNING    |
 * | timestamp   | numbers | FULL_COMPLETE                |
 *
 * @param {{ lastScanned: string|null|undefined, totalItems: number|null|undefined, unclassifiedItems: number|null|undefined }} input
 * @returns {string} SITE_SCAN_PHASE value
 */
export function getSiteScanPhase({ lastScanned, totalItems, unclassifiedItems }) {
  if (lastScanned) return SITE_SCAN_PHASE.FULL_COMPLETE
  const hasCounts = totalItems != null || unclassifiedItems != null
  return hasCounts ? SITE_SCAN_PHASE.PREVIEW_DONE_FULL_RUNNING : SITE_SCAN_PHASE.NEVER_SCANNED
}

/**
 * Empty-state copy for last-scan cells when `lastScanned` is null.
 * Distinguishes "preview done, full still running" from never scanned.
 *
 * @param {{ lastScanned: string|null|undefined, totalItems: number|null|undefined, unclassifiedItems: number|null|undefined }} input
 * @returns {string}
 */
export function getLastScanEmptyLabel(input) {
  return getSiteScanPhase(input) === SITE_SCAN_PHASE.PREVIEW_DONE_FULL_RUNNING ? 'Scanning' : '—'
}

/**
 * Whether cached scan data should still be treated as "in progress" for staleness/
 * polling purposes. A completed preview auto-chains a full scan server-side, and
 * `/scans/latest` briefly still returns the completed preview until the full scan's
 * record lands (it only prefers a full scan when one exists). Keep polling through
 * that window instead of stopping on the preview's terminal status.
 *
 * @param {ScanStatus|null|undefined} data
 * @returns {boolean}
 */
export function isScanInProgress(data) {
  if (isActiveScanStatus(data?.status)) return true
  // Preview auto-chains a full scan; keep polling only while latest is a completed preview.
  return data?.status === SCAN_STATUS.COMPLETED && data?.scanType === SCAN_TYPE.PREVIEW
}
